/**
 * Microsoft Authentication Provider
 * Wraps the application with MSAL context for Azure AD authentication
 */

import React, { useEffect, useState } from 'react';
import { MsalProvider as MsalReactProvider } from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
} from '@azure/msal-browser';
import { getMsalConfig, isMsalConfigured } from '@/lib/msalConfig';

// Initialize MSAL instance (singleton)
let msalInstance: PublicClientApplication | null = null;
let msalInitialized = false;
let redirectHandled = false;

/**
 * Get or create the MSAL instance
 */
export function getMsalInstance(): PublicClientApplication | null {
  if (!isMsalConfigured()) {
    console.warn('[MsalProvider] MSAL not configured - Azure AD auth disabled');
    return null;
  }

  if (!msalInstance) {
    const config = getMsalConfig();
    msalInstance = new PublicClientApplication(config);
  }

  return msalInstance;
}

/**
 * Initialize MSAL and set up event handlers
 */
async function initializeMsal(instance: PublicClientApplication): Promise<AuthenticationResult | null> {
  // IMPORTANT: Must call initialize() before any other MSAL operations
  if (!msalInitialized) {
    await instance.initialize();
    msalInitialized = true;
  }

  // Handle redirect promise (for redirect-based auth flows)
  let authResult: AuthenticationResult | null = null;

  if (!redirectHandled) {
    redirectHandled = true;
    try {
      authResult = await instance.handleRedirectPromise();
      if (authResult) {
        instance.setActiveAccount(authResult.account);
      }
    } catch (error) {
      console.error('[MsalProvider] Error handling redirect:', error);
    }
  }

  // Set active account if one exists
  const accounts = instance.getAllAccounts();
  if (accounts.length > 0) {
    // Use the first account, or find the one that was previously active
    const activeAccount = instance.getActiveAccount() || accounts[0];
    instance.setActiveAccount(activeAccount);
  }

  // Register event callbacks
  instance.addEventCallback((event: EventMessage) => {
    switch (event.eventType) {
      case EventType.LOGIN_SUCCESS:
        const loginResult = event.payload as AuthenticationResult;
        if (loginResult?.account) {
          instance.setActiveAccount(loginResult.account);
        }
        break;

      case EventType.ACQUIRE_TOKEN_SUCCESS:
        // Token acquired silently
        break;

      case EventType.LOGIN_FAILURE:
      case EventType.ACQUIRE_TOKEN_FAILURE:
        console.error('[MsalProvider] Auth failure:', event.error);
        break;
    }
  });

  return authResult;
}

interface MsalProviderProps {
  children: React.ReactNode;
}

/**
 * MSAL Provider Component
 * Initializes Microsoft authentication and provides context to the app
 */
export function MsalProvider({ children }: MsalProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    const init = async () => {
      const msalInst = getMsalInstance();

      if (msalInst) {
        await initializeMsal(msalInst);
        setInstance(msalInst);
      }

      setIsInitialized(true);
    };

    init();
  }, []);

  // Show loading state while MSAL is initializing
  if (!isInitialized) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-canvas"
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500/25 border-t-primary-500 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  // If MSAL is not configured, render children without the provider
  if (!instance) {
    return <>{children}</>;
  }

  return (
    <MsalReactProvider instance={instance}>
      {children}
    </MsalReactProvider>
  );
}

// Export the instance getter for use in hooks
export { msalInstance };
